import axios from 'axios';
import { Theme } from '../types';
import {
  baseClient,
  baseUrl,
  getOrCreateAnonymousId,
  getSessionId,
} from './baseRequest';
import { PopulatedKlydo } from './klydo';
import { Locale } from '../hooks/useLocale';

export type KlydosViewersResult = {
  clockId: string;
  city: string;
  latitude: number;
  longitude: number;
  loopUrl: string;
  photoUrl?: string;
};

export type FaqLink = {
  text: string;
  url: string;
};

export type FaqItem = {
  question: string;
  answer: string;
  links?: FaqLink[];
};

export type GuideDisplayMode = 'sections' | 'slides';

export type GuideBullet = {
  lead?: string;
  text: string;
};

export type GuideLink = {
  text: string;
  url: string;
};

export type GuideSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: GuideBullet[];
  // Links embedded in the section body text (paragraphs + bullets), matched by
  // `text`. A URL pointing at the public klydo route opens a klydo preview
  // popup in the reader instead of a new tab.
  links?: GuideLink[];
  note?: string;
  noteLinks?: GuideLink[];
};

export type GuideItem = {
  id: string;
  title: string;
  subtitle: string;
  icon?: string;
  displayMode: GuideDisplayMode;
  sections?: GuideSection[];
};

export type AnalyticsEvent = {
  type: string;
  context?: string;
  deviceType?: string;
};

export type StudioApiClient = {
  getSuggestedKlydoPallete: (
    loopUrl: string,
    abortSignal?: AbortSignal,
  ) => Promise<Theme[]>;
  getKlydoViewers: (windowHours: number) => Promise<KlydosViewersResult[]>;
  getTopKlydos: (limit: number) => Promise<PopulatedKlydo[]>;
  getSumeDise: () => Promise<number>;
  getLocale: () => Promise<Locale>;
  getKlydoDise: (klydoId: string) => Promise<number>;
  getKlydoGuideImageUrl: () => Promise<string>;
  getContractContent: () => Promise<{ html: string }>;
  getOnboardingImages: () => Promise<{ images: string[] }>;
  getFaq: () => Promise<{ items: FaqItem[] }>;
  getGuides: () => Promise<{ items: GuideItem[] }>;
  getPublicationReviewUpdates: () => Promise<PublicationReviewUpdates>;
  postAnalytics: (event: AnalyticsEvent) => Promise<void>;
};

// Epoch ms of the artist's most recent approve-or-reject — on a klydo
// publication request (`klydos`) and on a collection (`collections`); null when
// the artist has none. Compared against localStorage to decide the notification
// dot on the Klydos / Collections nav tabs.
export type PublicationReviewUpdates = {
  klydos: number | null;
  collections: number | null;
};

export const postAnonymousAnalytics = async (event: AnalyticsEvent) => {
  await axios.post(
    `${baseUrl}/studio/analytics/anonymous`,
    { type: event.type, context: event.context, deviceType: event.deviceType },
    {
      headers: {
        'client-session-id': getSessionId(),
        'client-anonymous-id': getOrCreateAnonymousId(),
      },
    },
  );
};

const studioApiClient: StudioApiClient = {
  getSuggestedKlydoPallete: async (
    loopUrl: string,
    abortSignal?: AbortSignal,
  ) => {
    return baseClient.post({
      url: `/studio/suggest-klydo-palette`,
      body: {
        loopUrl: loopUrl,
      },
      signal: abortSignal,
    });
  },
  getKlydoViewers(windowHours) {
    return baseClient.post<KlydosViewersResult[]>({
      url: '/studio/get-klydos-viewers',
      body: {
        windowHours,
      },
    });
  },
  getTopKlydos(limit) {
    return baseClient.post<PopulatedKlydo[]>({
      url: '/studio/get-top-klydos',
      body: {
        limit,
      },
    });
  },
  getSumeDise: async () => {
    return baseClient.get<number>({
      url: '/studio/get-sum-dise',
    });
  },
  getLocale: async () => {
    const response = await axios.get<Locale>('https://ip.klydoclock.com/');
    return response.data;
  },
  getKlydoDise: async (klydoId: string) => {
    return baseClient.get<number>({ url: `/studio/get-klydo-dise/${klydoId}` });
  },
  getKlydoGuideImageUrl: async () => {
    return baseClient.get<string>({
      url: '/studio/klydo-guide-image-url',
    });
  },
  getContractContent: async () => {
    return baseClient.get<{ html: string }>({
      url: '/studio/contract',
    });
  },
  getOnboardingImages: async () => {
    return baseClient.get<{ images: string[] }>({
      url: '/studio/studio-onboarding-images',
    });
  },
  getFaq: async () => {
    return baseClient.get<{ items: FaqItem[] }>({
      url: '/studio/faq',
    });
  },
  getGuides: async () => {
    return baseClient.get<{ items: GuideItem[] }>({
      url: '/studio/guides',
    });
  },
  getPublicationReviewUpdates: async () => {
    return baseClient.get<PublicationReviewUpdates>({
      url: '/studio/publication-review-updates',
    });
  },
  postAnalytics: async (event: AnalyticsEvent) => {
    await baseClient.post({
      url: '/studio/analytics',
      body: {
        type: event.type,
        context: event.context,
        deviceType: event.deviceType,
      },
    });
  },
};

export default studioApiClient;
